// v7.28 — PROMO COUPONS admin (Dan's spec): create promo codes worth a
// dollar amount of credit, hand them to promo users, watch them get claimed.
// Value is entered in DOLLARS and converted once at creation — the credits
// are frozen on the row so re-pricing never changes an issued code.
import { Fragment, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Ticket, Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Coupon {
  id: number;
  code: string;
  valueCents: number;
  credits: number;
  maxRedemptions: number | null;
  redemptionCount: number;
  expiresAt: string | null;
  isActive: boolean;
  note: string | null;
  createdAt: string;
  grantedCount: number;
  reservedCount: number;
}

interface Redemption {
  id: number;
  status: string;
  creditsGranted: number;
  reservedAt: string;
  grantedAt: string | null;
  userEmail: string | null;
  userName: string | null;
}

/** v7.44 — who claimed a code. Fetched lazily when its row is expanded. */
function RedemptionRows({ couponId, colSpan }: { couponId: number; colSpan: number }) {
  const { data: rows = [], isLoading, isError } = useQuery<Redemption[]>({
    queryKey: ["/api/admin/coupons", couponId, "redemptions"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/coupons/${couponId}/redemptions`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load redemptions");
      return res.json();
    },
  });

  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

  return (
    <tr className="border-t border-[#E5E2DC] bg-white">
      <td colSpan={colSpan} className="px-4 py-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-[#746F66]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading who used this code…
          </div>
        ) : isError ? (
          <div className="text-xs text-red-700">Couldn't load redemptions.</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-[#746F66]">Nobody has used this code yet.</div>
        ) : (
          <div className="flex flex-col gap-1.5" data-testid={`coupon-redemptions-${couponId}`}>
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-xs">
                <span className="text-[#3D3A36] font-medium min-w-[220px]">{r.userEmail ?? "(user deleted)"}</span>
                {r.userName && <span className="text-[#746F66] min-w-[120px]">{r.userName}</span>}
                {r.status === "granted" ? (
                  <span className="text-emerald-700">
                    {r.creditsGranted.toLocaleString()} credits · {when(r.grantedAt)}
                  </span>
                ) : (
                  <span className="text-amber-700/90" title="Signed up with the code but hasn't started their trial yet — the slot is held, credits land when the trial starts.">
                    awaiting trial · signed up {when(r.reservedAt)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminCouponsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  // v7.28 — defaults are deliberately BOUNDED. An unlimited, never-expiring
  // promo code is a bearer token for free credits: anyone it leaks to can
  // mint the full value on unlimited throwaway accounts. Unlimited is still
  // possible, but it has to be chosen.
  const [form, setForm] = useState({ code: "", valueDollars: "250", maxRedemptions: "1", expiresAt: "", note: "" });
  const [unlimited, setUnlimited] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: coupons = [], isLoading } = useQuery<Coupon[]>({
    queryKey: ["/api/admin/coupons"],
    queryFn: async () => {
      const res = await fetch("/api/admin/coupons", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load coupons");
      return res.json();
    },
  });

  const { data: rate } = useQuery<{ usdPerCredit: number }>({
    queryKey: ["/api/admin/coupons/rate"],
    queryFn: async () => {
      const res = await fetch("/api/admin/coupons/rate", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load rate");
      return res.json();
    },
    staleTime: 300_000,
  });

  const usdPerCredit = rate?.usdPerCredit ?? 0.009;
  const previewCredits = (() => {
    const v = parseFloat(form.valueDollars);
    if (!Number.isFinite(v) || v <= 0 || !(usdPerCredit > 0)) return null;
    return Math.round(v / usdPerCredit);
  })();

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/coupons", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          code: form.code.trim(),
          valueDollars: parseFloat(form.valueDollars),
          // v7.46 — blank must NOT mean unlimited: an uncapped code is a
          // cardless credit faucet via /promosignup. Unlimited requires the
          // explicit checkbox; blank falls back to single-use.
          maxRedemptions: unlimited ? null : (form.maxRedemptions.trim() ? Number(form.maxRedemptions) : 1),
          // Date input gives YYYY-MM-DD; send end-of-day UTC as ISO.
          expiresAt: form.expiresAt ? new Date(`${form.expiresAt}T23:59:59Z`).toISOString() : null,
          note: form.note.trim() || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Failed to create coupon");
      return res.json();
    },
    onSuccess: (c: Coupon) => {
      toast({ title: `Coupon ${c.code} created`, description: `${c.credits.toLocaleString()} credits per redemption.` });
      setForm({ code: "", valueDollars: "250", maxRedemptions: "1", expiresAt: "", note: "" });
      setUnlimited(false);
      setCreating(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/coupons"] });
    },
    onError: (e: Error) => toast({ title: "Couldn't create coupon", description: e.message, variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      const res = await fetch(`/api/admin/coupons/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ isActive }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Update failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/coupons"] }),
    onError: (e: Error) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/coupons/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Delete failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/coupons"] }),
    onError: (e: Error) => toast({ title: "Couldn't delete", description: e.message, variant: "destructive" }),
  });

  const money = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <Ticket className="w-6 h-6 text-emerald-700" />
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Promo Coupons</h1>
      </div>
      <p className="text-sm text-[#746F66] mb-6">
        One-time credit grants for promo users. The code is entered at signup; the credits land when
        their free trial starts, and they keep whatever's left when the trial rolls into billing.
      </p>

      {/* Create */}
      <div className="rounded-xl border border-[#E5E2DC] bg-white p-4 mb-8">
        {!creating ? (
          <button
            data-testid="coupon-create-open"
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#3D3A36] text-white text-sm font-semibold rounded-lg px-4 py-2"
          >
            <Plus className="w-4 h-4" /> New coupon
          </button>
        ) : (
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-[#746F66] mb-1">Code</label>
              <input
                data-testid="coupon-code-input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="PROMO250"
                className="w-44 bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2 tracking-wider font-semibold"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#746F66] mb-1">Value (USD)</label>
              <input
                data-testid="coupon-value-input"
                type="number"
                min={1}
                value={form.valueDollars}
                onChange={(e) => setForm({ ...form, valueDollars: e.target.value })}
                className="w-28 bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#746F66] mb-1">Number of uses</label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="coupon-max-input"
                  type="number"
                  min={1}
                  value={unlimited ? "" : form.maxRedemptions}
                  disabled={unlimited}
                  onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
                  placeholder={unlimited ? "unlimited" : "1"}
                  className="w-24 bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2 disabled:opacity-40"
                />
                <label className="flex items-center gap-1.5 text-xs text-[#746F66] whitespace-nowrap">
                  <input
                    data-testid="coupon-unlimited-toggle"
                    type="checkbox"
                    checked={unlimited}
                    onChange={(e) => setUnlimited(e.target.checked)}
                  />
                  unlimited
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#746F66] mb-1">Expires</label>
              <input
                data-testid="coupon-expires-input"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="w-40 bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-[#746F66] mb-1">Note (internal)</label>
              <input
                data-testid="coupon-note-input"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. Influencer batch — Sept"
                className="w-full bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2"
              />
            </div>
            <button
              data-testid="coupon-create-submit"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !form.code.trim() || !previewCredits || (!unlimited && !form.maxRedemptions.trim())}
              className="flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#3D3A36] disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-5 py-2.5"
            >
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create
            </button>
            <button onClick={() => setCreating(false)} className="text-sm text-[#746F66] hover:text-[#3D3A36] px-2 py-2">
              Cancel
            </button>
            {unlimited && !form.expiresAt && (
              <div className="w-full text-xs text-amber-700/90">
                Heads up: unlimited redemptions with no expiry means anyone this code reaches can claim
                {previewCredits ? ` ${previewCredits.toLocaleString()} credits` : " the full value"} on as many accounts as they like. Set a cap or an expiry unless that's intended.
              </div>
            )}
            <div className="w-full text-xs text-[#746F66]">
              {previewCredits
                ? <>= <span className="text-[#3D3A36] font-semibold">{previewCredits.toLocaleString()} credits</span> per redemption (at ${usdPerCredit.toFixed(5)}/credit). Frozen at creation — later price changes won't alter issued codes.</>
                : "Enter a dollar value to see the credit equivalent."}
            </div>
          </div>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-700" /></div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-16 text-[#746F66] border border-dashed border-[#E5E2DC] rounded-xl">
          No coupons yet — create one above.
        </div>
      ) : (
        <div className="rounded-xl border border-[#E5E2DC] overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead className="bg-white text-[#746F66] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Value</th>
                <th className="text-left px-4 py-3">Credits</th>
                <th className="text-left px-4 py-3">Uses</th>
                <th className="text-left px-4 py-3">Note</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <Fragment key={c.id}>
                <tr data-testid={`coupon-row-${c.id}`} className="border-t border-[#E5E2DC] text-[#3D3A36]">
                  <td className="px-4 py-3">
                    <span className="font-semibold tracking-wider">{c.code}</span>
                    {!c.isActive && <span className="ml-2 text-[10px] uppercase text-[#746F66] border border-[#E5E2DC] rounded px-1.5 py-0.5">off</span>}
                  </td>
                  <td className="px-4 py-3 text-[#3D3A36]">{money(c.valueCents)}</td>
                  <td className="px-4 py-3 text-[#3D3A36]">{c.credits.toLocaleString()}</td>
                  {/* v7.43 — "2 of 5" reads off redemptionCount, which is the
                      number the cap is actually enforced against (a reserved
                      signup already holds its slot). Showing granted/cap here
                      would under-report and let a full coupon look available. */}
                  <td className="px-4 py-3 text-[#3D3A36]">
                    {/* v7.44 — click the count to see WHO used it (Dan). */}
                    <button
                      data-testid={`coupon-uses-${c.id}`}
                      onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                      aria-expanded={expandedId === c.id}
                      disabled={c.redemptionCount === 0}
                      title={c.redemptionCount === 0 ? "No one has used this code yet" : "Show who used this code"}
                      className="flex items-center gap-1.5 text-left hover:text-[#1A1A1A] disabled:cursor-default disabled:hover:text-[#3D3A36]"
                    >
                      {c.redemptionCount > 0 && (
                        expandedId === c.id
                          ? <ChevronDown className="w-3.5 h-3.5 text-[#746F66]" />
                          : <ChevronRight className="w-3.5 h-3.5 text-[#746F66]" />
                      )}
                    <span className={c.maxRedemptions != null && c.redemptionCount >= c.maxRedemptions ? "text-amber-700 font-semibold" : "font-semibold"}>
                      {c.maxRedemptions != null
                        ? `${c.redemptionCount} of ${c.maxRedemptions}`
                        : `${c.redemptionCount} used`}
                    </span>
                    {c.maxRedemptions != null && c.redemptionCount >= c.maxRedemptions && (
                      <span className="text-amber-700/80 text-xs"> · fully used</span>
                    )}
                    </button>
                    {c.reservedCount > 0 && (
                      <div className="text-[11px] text-[#746F66]">
                        {c.grantedCount} credited · {c.reservedCount} signed up, trial not started
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#746F66] text-xs">{c.note || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`coupon-toggle-${c.id}`}
                        onClick={() => toggleMutation.mutate({ id: c.id, isActive: !c.isActive })}
                        className="text-xs border border-[#E5E2DC] hover:border-gray-500 rounded-lg px-2.5 py-1.5 text-[#3D3A36]"
                      >
                        {c.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        data-testid={`coupon-delete-${c.id}`}
                        title={c.redemptionCount > 0 ? "Claimed coupons can't be deleted — deactivate instead" : "Delete"}
                        onClick={() => deleteMutation.mutate(c.id)}
                        disabled={c.redemptionCount > 0}
                        className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E5E2DC] hover:border-red-600 hover:text-red-700 text-[#746F66] disabled:opacity-30 disabled:hover:border-[#E5E2DC] disabled:hover:text-[#746F66]"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === c.id && <RedemptionRows couponId={c.id} colSpan={6} />}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
